import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { isSupabaseStorageUrl, toCleanUrl, getTransformUrl } from "@/lib/storageUrl";

interface BlurImageProps {
  src: string;
  alt: string;
  /** Applied to the <img> element */
  className?: string;
  /** Applied to the outer wrapper div (useful for fixed aspect-ratio containers) */
  containerClassName?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  sizes?: string;
  /** Controls how the image fits within its container. 'cover' crops to fill, 'contain' fits within bounds */
  objectFit?: 'cover' | 'contain';
}

function getNextYouTubeThumbnailUrl(url: string): string | null {
  const match = url.match(
    /https?:\/\/img\.youtube\.com\/vi\/([a-zA-Z0-9_-]{11})\/([^./]+)\.jpg/i
  );
  if (!match) return null;

  const videoId = match[1];
  const current = match[2];

  // From best-effort to most-compatible.
  const order = ["maxresdefault", "sddefault", "hqdefault", "mqdefault", "default"] as const;

  const idx = order.indexOf(current as (typeof order)[number]);
  if (idx === -1) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }

  const next = order[idx + 1];
  return next ? `https://img.youtube.com/vi/${videoId}/${next}.jpg` : null;
}

/**
 * BlurImage component:
 * - Shows a subtle skeleton overlay while loading
 * - Uses a blur-to-sharp transition (never stays blurry)
 * - Does NOT force a fixed aspect ratio (masonry-friendly)
 * - Adds YouTube thumbnail fallbacks (sddefault can be missing on some videos)
 */
export function BlurImage({
  src,
  alt,
  className,
  containerClassName,
  width,
  height,
  priority = false,
  sizes,
  objectFit = 'cover',
}: BlurImageProps) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  // Transform Supabase URLs to clean domain URLs
  const cleanSrc = useMemo(() => toCleanUrl(src), [src]);
  const originalSrc = src;
  const [currentSrc, setCurrentSrc] = useState(cleanSrc);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [triedOriginalFallback, setTriedOriginalFallback] = useState(false);

  const isYouTubeThumb = useMemo(() => currentSrc.includes("img.youtube.com/vi/"), [currentSrc]);

  // Generate responsive srcset using Supabase image transforms
  const srcSet = useMemo(() => {
    // Only generate srcset for Supabase-hosted images (not YouTube, external, etc.)
    if (isYouTubeThumb) return undefined;
    const widths = [400, 800, 1200, 1600];
    const parts = widths
      .map(w => {
        const transformed = getTransformUrl(currentSrc, w);
        // Only include if the URL was actually transformed
        return transformed !== currentSrc ? `${transformed} ${w}w` : null;
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : undefined;
  }, [currentSrc, isYouTubeThumb]);

  useEffect(() => {
    setCurrentSrc(cleanSrc);
    setIsLoaded(false);
    setHasError(false);
    setTriedOriginalFallback(false);
  }, [cleanSrc]);

  // If the image is already in cache, onLoad can be missed — handle that.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [currentSrc]);

  const showSkeleton = !isLoaded && !hasError;

  return (
    <div className={cn("relative overflow-hidden bg-muted", containerClassName)}>
      <div
        className={cn(
          "pointer-events-none absolute inset-0 transition-opacity duration-300",
          showSkeleton ? "opacity-100" : "opacity-0",
          // Let the image show through even if loading takes a while.
          "bg-muted/60",
          showSkeleton ? "animate-pulse" : ""
        )}
      />

      <img
        ref={imgRef}
        src={currentSrc}
        srcSet={srcSet}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        fetchPriority={priority ? "high" : "auto"}
        className={cn(
          "block w-full h-full",
          "transition-[filter] duration-300",
          showSkeleton ? "blur-sm" : "blur-0",
          objectFit === 'cover' ? "object-cover" : "object-contain",
          className
        )}
        onLoad={() => {
          setHasError(false);
          setIsLoaded(true);
        }}
        onError={() => {
          // If clean /files/* URLs are broken in production (e.g. proxy misconfigured),
          // fall back to the original Supabase storage URL so images still render.
          if (
            !triedOriginalFallback &&
            currentSrc === cleanSrc &&
            cleanSrc !== originalSrc &&
            isSupabaseStorageUrl(originalSrc)
          ) {
            setCurrentSrc(originalSrc);
            setTriedOriginalFallback(true);
            setIsLoaded(false);
            setHasError(false);
            return;
          }

          if (isYouTubeThumb) {
            const next = getNextYouTubeThumbnailUrl(currentSrc);
            if (next && next !== currentSrc) {
              setCurrentSrc(next);
              setIsLoaded(false);
              setHasError(false);
              return;
            }
          }

          // Stop showing skeleton forever; keep the card usable.
          setHasError(true);
          setIsLoaded(true);
        }}
      />
    </div>
  );
}

